import { type TopLevelQuerySpec, contentId } from "@moment/api-collab/differential/index";

/**
 * Analyzes a TopLevelQuerySpec to find all leftOuterJoin operations and maps their output paths to the corresponding tables.
 * This is used to determine which table a cell being edited belongs to.
 *
 * @param query The TopLevelQuerySpec to analyze
 * @returns A record mapping JSON paths to table names
 */
export function parseLinkedRecords(query: TopLevelQuerySpec): Record<string, string> {
	const result: Record<string, string> = {};

	// Helper function to process a single query spec
	const processQuerySpec = (spec: TopLevelQuerySpec["spec"], parentPath?: string) => {
		for (const op of spec) {
			if (op.type === "leftOuterJoin") {
				// For leftOuterJoin, we need to look at the output type to determine the path
				if (
					op.output.type === "insertAtPath" ||
					op.output.type === "insertAggregateAtPath"
				) {
					// Extract the numeric index from the subqueryId (e.g., "subquery1" -> 0)
					const subquery = query.subquerySpecs.find(
						(subquery) => contentId(subquery) === op.right.subqueryId
					);
					if (subquery) {
						// The first element of the subquery spec is the FromSpec containing the table name
						const path = parentPath
							? `${parentPath}.${op.output.path.path.replace("$.", "")}`
							: op.output.path.path;
						result[path] = subquery[0].table;

						// Process the subquery with the current path as parent
						processQuerySpec(subquery, path);
					}
				}
				// For "tuple" type, we don't need to track it as it's not a nested field
			}
		}
	};

	// Process the main query spec
	processQuerySpec(query.spec);

	return result;
}
